import AutoLayoutSelector from "./AutoLayoutSelector";
import ColorInput from "./ColorInput";
import EmptyInputState from "./EmptyInputState";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { CaretDownIcon, ResetIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";

interface ClickableIconProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const ClickableIcon = ({ children, ...props }: ClickableIconProps) => {
  return (
    <div
      className="w-4 h-4 flex justify-center items-center p-1 hover:bg-secondary-400 hover:text-secondary-900 text-white cursor-pointer"
      {...props}
    >
      {children}
    </div>
  );
};

interface ComboboxContextType {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
}

const ComboboxContext = createContext<ComboboxContextType | undefined>(
  undefined
);

const useComboboxContainer = () => {
  const context = useContext(ComboboxContext);

  if (context === undefined) {
    throw new Error(
      "useComboboxContainer must be used within a ComboboxContainer"
    );
  }

  return context;
};

interface ComboboxContainerProps {
  className?: string;
  children: React.ReactNode;
}

const ComboboxContainer: React.FC<ComboboxContainerProps> = ({
  children,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className={className} style={{ width: "100%" }}>
      <ComboboxContext.Provider
        value={{
          containerRef: containerRef,
        }}
      >
        {children}
      </ComboboxContext.Provider>
    </div>
  );
};

interface ComboBoxOptionType {
  tailwind: string;
  css: string;
}

interface ComboBoxProps {
  options: ComboBoxOptionType[];
  onOptionSelected: (option: ComboBoxOptionType) => void;
  input: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  onOptionSelected,
  input,
  open,
  setOpen,
  buildArbitraryClass,
  buildOptionPreview,
  anchorRef,
}) => {
  const { containerRef } = useComboboxContainer();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [firstNonNullInput, setFirstNonNullInput] = useState<string | null>(
    null
  );
  const [inputChanged, setInputChanged] = useState(false);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef(null);

  const [comboboxStyle, setComboboxStyle] = useState(null);

  useEffect(() => {
    if (open && anchorRef.current && containerRef.current) {
      const inputRect = anchorRef.current.getBoundingClientRect();

      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceAbove = inputRect.top;
      const spaceBelow = viewportHeight - inputRect.bottom;

      const useBottomPosition = spaceBelow > spaceAbove;

      const position = useBottomPosition
        ? { top: `${inputRect.bottom}px` }
        : { bottom: `${viewportHeight - inputRect.top}px` };

      const width = containerRect.width;

      setComboboxStyle({
        ...position,
        // @ts-expect-error TS2345
        left: `${containerRect.left}px`,
        width: `${width}px`,
      });
    }
  }, [open, anchorRef, containerRef]);

  /**
   * Tracks the first non-null input (ie. the initial value.)
   *
   * Tracks changes to input and sets input changes.
   */
  useEffect(() => {
    if (input !== null && firstNonNullInput === null) {
      setFirstNonNullInput(input); // Store the first non-null input
    }
    if (firstNonNullInput !== null && input !== firstNonNullInput) {
      setInputChanged(true); // Mark as changed if different from first non-null input
    }
  }, [input]);

  /**
   * If there was a non-null input, and it's now empty, reset the active index.
   */
  useEffect(() => {
    if (input === "" && firstNonNullInput !== null) {
      setActiveIndex(null);
    }
  }, [input, firstNonNullInput]);

  const filteredOptions = useMemo(() => {
    let newOptions = options
      .map((option) => {
        let score = 0;

        if (!input) {
          score = 1; // So that all items are considered when there's no searchTerm
        } else {
          const term = input.toLowerCase();

          if (option.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (option.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (option.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (option.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...option, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    if (buildArbitraryClass && input.length) {
      newOptions.unshift({
        ...buildArbitraryClass(input),
        score: Number.MAX_SAFE_INTEGER,
      });
    }

    return newOptions;
  }, [
    input,
    open,
    options,
    buildArbitraryClass,
    inputChanged,
    firstNonNullInput,
  ]);

  const showAllOptions = firstNonNullInput !== null && !inputChanged;
  const optionsToShow = showAllOptions ? options : filteredOptions;
  const defaultIndex =
    filteredOptions.length > 1
      ? options.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : -1;

  const indexOfActiveOption =
    filteredOptions.length > 1
      ? optionsToShow.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : 0;

  /**
   * Reset first non-null input and input change state on close.
   */
  useEffect(() => {
    if (open && showAllOptions && activeIndex === null && defaultIndex >= 0) {
      setActiveIndex(defaultIndex);
    }

    if (!open) {
      setFirstNonNullInput(null);
      setInputChanged(false);
    }
  }, [open, showAllOptions, defaultIndex]);

  useEffect(() => {
    if (input !== null && input !== "" && firstNonNullInput !== null) {
      setActiveIndex(indexOfActiveOption);
    }
  }, [input]);

  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        // @ts-expect-error TS2339
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setActiveIndex(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [comboboxRef]);

  /**
   * Arrow and enter selection for combobox
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        // Move focus down
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex >= optionsToShow.length - 1
            ? 0
            : prevIndex + 1
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        // Move focus up
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex <= 0
            ? optionsToShow.length - 1
            : prevIndex - 1
        );
        event.preventDefault();
      } else if (event.key === "Enter" && activeIndex !== null) {
        // Select the active item
        onOptionSelected(optionsToShow[activeIndex]);
        setOpen(false);
        event.preventDefault();
      } else if (event.key === "Escape") {
        setOpen(false);
        setActiveIndex(null);
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, optionsToShow, onOptionSelected]);

  /**
   * Scroll list to match activeIndex
   */
  useEffect(() => {
    if (activeOptionRef.current) {
      const activeElement = activeOptionRef.current;
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  if (filteredOptions.length === 0) {
    return;
  }

  if (!comboboxStyle) {
    return null;
  }

  return (
    <div
      className="fixed flex-grow z-50 text-xs"
      ref={comboboxRef}
      style={comboboxStyle}
    >
      <div className="p-2">
        {open && (
          <div className="bg-popover shadow-md rounder-sm border max-h-72 overflow-y-auto flex flex-col hide-scrollbar">
            {optionsToShow.map((cls, index) => (
              <div
                key={index}
                className={`p-2 flex-grow flex flex-row justify-between text-2xs space-x-1 items-center cursor-pointer ${
                  index === activeIndex ? "bg-accent" : "hover:bg-accent"
                }`}
                onClick={() => onOptionSelected(cls)}
                ref={index === activeIndex ? activeOptionRef : null}
              >
                <div className="flex flex-row space-x-2 items-center">
                  {buildOptionPreview && (
                    <div className="w-4 h-4">{buildOptionPreview(cls)}</div>
                  )}
                  <div className="font-medium flex-grow whitespace-nowrap line-clamp-1">
                    {cls.tailwind}
                  </div>
                </div>
                <div className="flex flex-col flex-shrink">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <div className="flex flex-col flex-shrink text-ellipsis overflow-hidden">
                        {cls.css
                          .split("\n")
                          .map((cssLine: string, index: number) => {
                            return (
                              <div
                                key={index}
                                className="font-light text-right flex-shrink text-slate-500 line-clamp-1"
                              >
                                {cssLine}
                              </div>
                            );
                          })}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex-grow p-0"
                      side="bottom"
                      avoidCollisions={false}
                    >
                      <div className="space-y-1 flex flex-col text-xs w-full overflow-hidden pt-4">
                        <h4 className="font-semibold px-4">{cls.tailwind}</h4>
                        <pre className="w-full overflow-auto px-4 pb-4">
                          <code className="css">{cls.css}</code>
                        </pre>{" "}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface DropdownOptionType {
  label: string;
  value: any;
  icon: ReactNode;
}

interface DropdownInputProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  dropdownOptions: DropdownOptionType[];
  onDropdownSelected: (option: DropdownOptionType) => void;
  className?: string;
}

const DropdownInput: React.FC<DropdownInputProps> = ({
  visible,
  setVisible,
  dropdownOptions,
  onDropdownSelected,
  className,
}) => {
  const dropdownRef = useRef(null);
  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        // @ts-expect-error TS2339
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [dropdownRef]);

  return (
    visible &&
    dropdownOptions && (
      <div
        className={
          "absolute top-full right-0 mt-2 w-full min-w-24 truncate nowrap bg-secondary-300 border-2 border-secondary-500 text-xs text-white z-10 rounded-md " +
          (className ? className : "")
        }
        ref={dropdownRef}
      >
        {dropdownOptions.map((option, index) => (
          <div
            key={index}
            className="p-2 cursor-pointer hover:bg-secondary-500 flex flex-row gap-x-1"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDropdownSelected(option);
            }}
          >
            <span>{!!option.icon && option.icon}</span>
            <span>{option.label}</span>
          </div>
        ))}
      </div>
    )
  );
};

interface DraggableInputProps {
  value: string;
  evaluatedValue: string;
  setValue: (val: string) => void;
  set: boolean;
  setSet: (set: boolean) => void;
  labelContent: ReactNode;
  comboboxOptions?: ComboBoxOptionType[];
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  onComboboxSelected?: (option: ComboBoxOptionType) => void;
  onSliderValueUpdate?: (option: ComboBoxOptionType) => void; // Must be supplied with build arbitrary class fn
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  wrap?: boolean;
  fallback?: { number: number; unit: string }; // Used if the value cannot be parsed
  draggable?: boolean;
  onClear?: () => void;
}

interface TooltipProps {
  tooltipContent?: string | null;
}

const DraggableInput: React.FC<DraggableInputProps & TooltipProps> = ({
  value,
  evaluatedValue,
  setValue,
  labelContent,
  dropdownOptions = [],
  comboboxOptions,
  onDropdownSelected,
  onComboboxSelected,
  onSliderValueUpdate,
  onClear,
  buildArbitraryClass,
  buildOptionPreview,
  set = false,
  setSet,
  onInputFocus,
  disabled = false,
  min,
  max,
  step,
  wrap = false,
  draggable = true,
  tooltipContent = null,
  fallback,
}) => {
  const [snapshot, setSnapshot] = useState(value);
  const [startVal, setStartVal] = useState<null | number>(null);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const totalDragRef = useRef<number>(0);
  const latestValueRef = useRef<string>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const draggableInputRef = useRef<HTMLDivElement>(null);

  const enhancedDropdownOptions = useMemo(() => {
    if (onClear && set) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, onClear, set]);

  /**
   * Best attempt at extracting the first value from a provided CSS string
   *
   * Example:
   *
   * Input: padding-left: 2.25rem; // 36px \n padding-right: 2.25rem; // 36px
   * Output: 2.25rem
   *
   * @param css
   * @returns value
   */
  const safeExtractValueFromCss = (css: string): string | null => {
    try {
      const firstCss = css.split("\n")[0];
      let value: string = firstCss.split(":")[1];
      value = value.split(";")[0];

      return value.trim();
    } catch (error) {
      return null;
    }
  };

  // Extract the number and unit from the string value.
  const extractNumberAndUnit = (val: string) => {
    const match = val.match(/^(-?\d+(\.\d+)?)([a-zA-Z%]*)$/);
    if (match) {
      return {
        number: parseFloat(match[1]),
        unit: match[3],
      };
    }

    if (fallback) {
      return fallback;
    }

    return null;
  };

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setValue(ev.target.value || "");
      setComboboxOpen(true);
    },
    [setValue]
  );

  const onStart = useCallback(
    (event: React.MouseEvent) => {
      setStartVal(event.clientX);
      setSnapshot(evaluatedValue);
    },
    [value]
  );

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (option.value === "clear" && onClear) {
      onClear();
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }

    setDropdownVisible(false);
  };

  const handleComboboxOptionClick = (option: ComboBoxOptionType) => {
    if (onComboboxSelected) {
      setSet(true);
      onComboboxSelected(option);
    }
    setComboboxOpen(false);
    const value = safeExtractValueFromCss(option.css);
    if (value) {
      setValue(value);
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (value === "" && onClear) {
      setComboboxOpen(false);
      onClear();

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  /**
   * Draggable input effect
   */
  useEffect(() => {
    const onUpdate = (event: MouseEvent) => {
      if (startVal !== null) {
        const diff = event.clientX - startVal;
        totalDragRef.current += Math.abs(diff);

        const extracted = extractNumberAndUnit(snapshot);
        if (extracted) {
          let updatedNumber = extracted.number + diff * (step || 1);

          // Check if there is a min value and if the current value is below it
          if (typeof min === "number" && updatedNumber < min) {
            updatedNumber = wrap ? max || updatedNumber : min;
          }

          // Check if there is a max value and if the current value is above it
          if (typeof max === "number" && updatedNumber > max) {
            updatedNumber = wrap ? min || updatedNumber : max;
          }

          const updatedValue = `${updatedNumber}${extracted.unit}`;
          setValue(updatedValue);
          latestValueRef.current = updatedValue;
          if (buildArbitraryClass && onSliderValueUpdate) {
            setSet(true);
            onSliderValueUpdate(buildArbitraryClass(updatedValue));
          }
        }
      }
    };

    const onEnd = () => {
      if (totalDragRef.current > 5) {
        if (
          buildArbitraryClass &&
          onComboboxSelected &&
          latestValueRef.current
        ) {
          setSet(true);
          onComboboxSelected(buildArbitraryClass(latestValueRef.current)); // Using value, as it's the final value after dragging
        }
      }

      totalDragRef.current = 0;
      // @ts-expect-error TS2322
      latestValueRef.current = null;
      setStartVal(null);
    };

    document.addEventListener("mousemove", onUpdate);
    document.addEventListener("mouseup", onEnd);
    return () => {
      document.removeEventListener("mousemove", onUpdate);
      document.removeEventListener("mouseup", onEnd);
    };
  }, [startVal, setValue, snapshot]);

  /**
   * Track changes to disabled to ensure an unusable input isn't in focus.
   */
  useEffect(() => {
    if (disabled) {
      setFocused(false);
      setHovered(false);
      setComboboxOpen(false);
    }
  }, [disabled]);

  return (
    <>
      {/*
       // @ts-expect-error TS2322 */}
      <div
        data-tooltip-id="styling-panel-tooltip"
        data-tooltip-content={tooltipContent}
        data-tooltip-place="bottom"
        data-tooltip-offset={5}
        data-tooltip-hidden={comboboxOpen || dropdownVisible}
        ref={draggableInputRef}
      >
        <div
          className={`flex flex-row items-center w-full border border-transparent hover:border-primary-0 relative bg-secondary-200 rounded-md h-8 ${
            focused ? "border-primary-0" : undefined
          } min-w-18`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onContextMenu={(e) => {
            e.preventDefault();
            if (enhancedDropdownOptions && enhancedDropdownOptions.length > 0) {
              setDropdownVisible(true);
            }
          }}
          onMouseDown={(event) => {
            if (event.button === 2) {
              // Right click
              event.preventDefault();
            }
          }}
        >
          {/* Dropdown content */}
          {labelContent && (
            <div
              onMouseDown={(event: React.MouseEvent) => {
                if (draggable) {
                  onStart(event);
                }
              }}
              className={`py-1 pl-1.5 pr-2 ${
                disabled || !set ? "text-secondary-600" : "text-white"
              } ${draggable ? "cursor-ew-resize" : ""} select-none min-w-2`}
            >
              {labelContent}
            </div>
          )}
          <input
            disabled={disabled}
            value={value}
            onChange={onInputChange}
            onFocus={(event) => {
              event.target.select();
              setComboboxOpen(true);
              setFocused(true);

              if (onInputFocus) {
                onInputFocus(event);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => setFocused(false)}
            className={`text-2xs px-1 border-none outline-none w-full font-medium bg-transparent ${
              disabled || !set ? "text-text-tertiary" : "text-text-primary"
            }`}
            ref={inputRef}
          />

          <div
            className={cn(!enhancedDropdownOptions?.length && "hidden")}
            onClick={() => setDropdownVisible(true)}
          >
            <CaretDownIcon className="mr-1" />
          </div>
          <DropdownInput
            className="w-40"
            dropdownOptions={enhancedDropdownOptions}
            onDropdownSelected={handleDropdownOptionClick}
            // @ts-expect-error TS2322
            visible={dropdownVisible}
            setVisible={setDropdownVisible}
          />
        </div>
        {comboboxOpen && comboboxOptions && (
          <ComboBox
            input={(value || "").toString()}
            options={comboboxOptions}
            open={comboboxOpen}
            setOpen={setComboboxOpen}
            onOptionSelected={(option: ComboBoxOptionType) =>
              handleComboboxOptionClick(option)
            }
            buildOptionPreview={buildOptionPreview}
            buildArbitraryClass={buildArbitraryClass}
            anchorRef={draggableInputRef}
          />
        )}
      </div>
    </>
  );
};

interface DiscreteOption extends TooltipProps {
  key: string;
  option: ComboBoxOptionType;
  labelContent: ReactNode;
}

interface DiscreteInputProps {
  options: DiscreteOption[];
  set?: boolean;
  onOptionSelected: (option: DiscreteOption) => void;
  selected: string;
  disabled?: boolean;
  onDeselect?: (option: DiscreteOption) => void;
  className?: string;
}

const DiscreteInput: React.FC<DiscreteInputProps> = ({
  options,
  set = false,
  onOptionSelected,
  selected,
  disabled = false,
  onDeselect,
  className,
}) => {
  const anySelected = useMemo(() => {
    return options.find((option) => option.key === selected);
  }, [options, selected]);

  return (
    <div
      className={cn(
        "flex flex-row items-center gap-0.5 relative border border-transparent bg-secondary-200 rounded-md",
        className
      )}
    >
      {options.map((option, index) => {
        const isSelected = selected === option.key;
        return (
          <div className="h-7 flex grow" key={index}>
            <div
              key={option.key}
              onClick={() => {
                if (disabled) {
                  return;
                }

                if (isSelected && onDeselect) {
                  onDeselect(option);
                  return;
                }

                onOptionSelected(option);
              }}
              className={cn(
                `cursor-pointer p-1 flex flex-grow shrink items-center rounded-md justify-center text-text-tertiary hover:text-icons-primary hover:bg-icons-muted`,
                isSelected && "bg-icons-muted text-icons-primary",
                !anySelected && "text-text-tertiary hover:text-white",
                index === 0 && "rounded-l-md",
                index === options.length - 1 && "rounded-r-md"
              )}
              data-tooltip-id="styling-panel-tooltip"
              data-tooltip-content={option.tooltipContent}
              data-tooltip-place="bottom"
              data-tooltip-offset={5}
            >
              {option.labelContent}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ToggleInputProps extends TooltipProps {
  toggled: boolean;
  labelContent: ReactNode;
  onToggle: (toggled: boolean) => void;
  set?: boolean;
  disabled?: boolean;
}

const ToggleInput: React.FC<ToggleInputProps> = ({
  toggled,
  labelContent,
  onToggle,
  set,
  disabled,
  tooltipContent,
}) => {
  return (
    <div className="h-8 flex grow">
      <div
        onClick={() => {
          !disabled && onToggle(!toggled);
        }}
        className={`flex flex-row w-full relative border border-transparent hover:border-secondary-600 cursor-pointer p-1 items-center justify-center bg-secondary-200 rounded-md ${
          toggled ? "bg-secondary-500" : "text-secondary-600 hover:text-white"
        }`}
        data-tooltip-id="styling-panel-tooltip"
        data-tooltip-content={tooltipContent}
        data-tooltip-place="bottom"
        data-tooltip-offset={5}
      >
        {labelContent}
      </div>
    </div>
  );
};

export type {
  DropdownOptionType,
  ComboBoxOptionType,
  DiscreteOption,
  TooltipProps,
};

export {
  ClickableIcon,
  ComboBox,
  DropdownInput,
  DraggableInput,
  DiscreteInput,
  ToggleInput,
  AutoLayoutSelector,
  ColorInput,
  EmptyInputState,
  ComboboxContainer,
};
